import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Icon,
  MenuItem,
  Paper,
  Select,
  TextField,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { ClientesService } from '../../services/api/cadastros/ClientesService';
import { useDebounce } from '../../hooks';
import { Environment } from '../../environment';

interface IDadosSelect {
  nameBD: string;
  descricao: string;
}

type TAutoCompleteOption = {
  idcli: string;
  label: string;
}

interface IFerramentasDaListagemProps {
  dadosSelecao?: IDadosSelect[];
  dataInicial?: string;
  dataFinal?: string;
  textonome?: string;
  mostrarInputData?: boolean;
  aoMudarDataInicial?: (novoTexto: string) => void;
  aoMudarDataFinal?: (novoTexto: string) => void;
  textoBotaoNovo?: string;
  mostrarBotaoNovo?: boolean;
  aoClicarEmNovo?: () => void;
  mostrarInputBasicSelect?: boolean;
  textoDoSelect?: string;
  aoMudarTextoDoSelect?: (novoTextoselect: string) => void;
  mostrarInputBusca?: boolean;
  textoDaBusca?: string;
  aoMudarTextoDeBusca?: (novoTexto: string) => void;
  mostrarAutoComplete?: boolean;
  aoMudarParceiro?: (novoTextoselect: string) => void;
  isExternalLoading?: boolean;
  uuidcli?: (novoTextoselect: string | undefined) => void;
  nome?: (novoTextoselect: string | undefined) => void;
}

export const FerramentasDaListagemPedido: React.FC<IFerramentasDaListagemProps> = ({
  nome,
  textoDaBusca = '',
  textonome = '',
  dataInicial = '',
  aoMudarDataInicial,
  dataFinal = '',
  aoMudarDataFinal,
  mostrarInputData = false,
  aoClicarEmNovo,
  textoBotaoNovo = 'Novo',
  mostrarBotaoNovo = true,
  dadosSelecao = [],
  mostrarInputBasicSelect = false,
  textoDoSelect = '',
  aoMudarTextoDoSelect,
  mostrarInputBusca = false,
  aoMudarTextoDeBusca,
  mostrarAutoComplete = false,
  aoMudarParceiro,
}) => {
  const theme = useTheme();
  const partesDaDataInicial = dataInicial.split('/');
  const dataInicialFormatada = `${partesDaDataInicial[2]}-${partesDaDataInicial[1]}-${partesDaDataInicial[0]}`;

  const partesDaDataFinal = dataFinal.split('/');
  const dataFinalFormatada = `${partesDaDataFinal[2]}-${partesDaDataFinal[1]}-${partesDaDataFinal[0]}`;

  const [isLoading, setIsLoading] = useState(false);
  const { debounce } = useDebounce();
  const [busca, setBusca] = useState('');
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      ClientesService.getAllList(selectedId, busca)
        .then((result) => {
          setIsLoading(false);
          if (!(result instanceof Error)) {
            setOpcoes(result.data.map(cliente => ({ idcli: cliente.idcli, label: cliente.nome })));
          }
        });
    });
  }, [busca, selectedId]);

  const autoCompleteSelectedOption = useMemo(() => {
    return opcoes.find(opcao => opcao.idcli === selectedId) || null;
  }, [selectedId, opcoes]);

  return (
    <Paper sx={{ p: 2, m: 2 }}>
     <Grid container spacing={2} alignItems="center">
  {mostrarInputBasicSelect && (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
      <Select
        fullWidth
        size="small"
        value={textoDoSelect}
        onChange={(event) => aoMudarTextoDoSelect?.(event.target.value)}
      >
        {dadosSelecao.map((item) => (
          <MenuItem key={item.nameBD} value={item.nameBD}>
            {item.descricao}
          </MenuItem>
        ))}
      </Select>
    </Grid>
  )}

  {mostrarInputBusca && (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
      <TextField
        fullWidth
        size="small"
        value={textoDaBusca}
        placeholder={Environment.INPUT_DE_BUSCA}
        onChange={(e) => aoMudarTextoDeBusca?.(e.target.value)}
      />
    </Grid>
  )}

  {mostrarAutoComplete && (
    <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
      <Autocomplete
        fullWidth
        disablePortal
        options={opcoes}
        loading={isLoading}
        value={autoCompleteSelectedOption}
        onInputChange={(_, newValue) => setBusca(newValue)}
        onChange={(_, newValue) => {
          setSelectedId(newValue?.idcli);
          aoMudarParceiro?.(newValue?.idcli || '');
          nome?.(newValue?.label);
          setBusca('');
        }}
        popupIcon={isLoading ? <CircularProgress size={24} /> : undefined}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Cliente" />
        )}
      />
    </Grid>
  )}

  {mostrarInputData && (
    <>
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data Inicial"
            format="DD/MM/YYYY"
            value={dayjs(dataInicialFormatada)}
            onChange={(date) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataInicial?.(novoTexto);
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data Final"
            format="DD/MM/YYYY"
            value={dayjs(dataFinalFormatada)}
            onChange={(date) => {
              const novoTexto = date ? date.format('DD/MM/YYYY') : '';
              aoMudarDataFinal?.(novoTexto);
            }}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
    </>
  )}

  {mostrarBotaoNovo && (
    <Grid item xs={12} sm={12} md="auto" display="flex" justifyContent="flex-end">
      <Button
        variant="contained"
        color="primary"
        endIcon={<Icon>add</Icon>}
        onClick={aoClicarEmNovo}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {textoBotaoNovo}
      </Button>
    </Grid>
  )}
</Grid>
    </Paper>
  );
};
